import React, { memo, useState, useCallback, useRef, useEffect, useMemo } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Icon,
  Collapse,
  useToast,
  Tooltip,
  Input,
  Link,
} from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import {
  FiFolder,
  FiFile,
  FiImage,
  FiChevronRight,
  FiChevronDown,
  FiFileText,
  FiTag,
} from 'react-icons/fi'
import { Folder, FileItem } from '@/types'
import { useNavigation } from '@/hooks/useNavigation'
import { useAppTranslation } from '@/contexts/LanguageContext'
import { useAppSelector, useAppDispatch } from '@/store'
import { selectFilesByFolder, selectFilesLoading } from '@/store/selectors'
import { renameFile, addFile, deleteFile } from '@/store/slices/filesSlice'
import apiService from '@/services/api'
import { updateFolder } from '@/store/slices/foldersSlice'
import FolderPropertiesModal from '@/components/Modals/FolderPropertiesModal'
import FolderPermissionsModal from '@/components/Modals/FolderPermissionsModal'
import FilePropertiesModal from '@/components/Modals/FilePropertiesModal'
import DeleteFileConfirmationModal from '@/components/Modals/DeleteFileConfirmationModal'
import FilePermissionsModal from '@/components/Modals/FilePermissionsModal'
import { MotionBox } from '@/components/Common/MotionBox'
import { ContextMenu } from '@/components/Common'

interface FolderTreeProps {
  folders: Folder[]
  selectedFolder: Folder | null
  onFolderSelect: (folder: Folder | null) => void
  onFileSelect?: (file: FileItem) => void
  /** Disables drag-and-drop on folders and files (e.g. file picker modal). */
  pickerMode?: boolean
}

interface TreeFolderItemProps {
  folder: Folder
  selectedFolder: Folder | null
  onFolderSelect: (folder: Folder) => void
  level: number
  expandedFolders: Set<number>
  toggleFolderExpansion: (folderId: number) => void
  moveFolder: (folderId: number, targetParentId?: number) => Promise<boolean>
  moveFile: (fileId: number, targetFolderId?: number) => Promise<boolean>
  moveSelectedFiles: (targetFolderId?: number) => Promise<boolean>
  onFileSelect?: (file: FileItem) => void
  pickerMode?: boolean
}

interface TreeFileItemProps {
  file: FileItem
  level: number
  onFileSelect?: (file: FileItem) => void
  pickerMode?: boolean
}

const getFileIcon = (mimeType?: string) => {
  if (!mimeType) return FiFile

  if (mimeType === 'application/pdf') {
    return FiFile
  }

  if (mimeType.includes('sheet') || mimeType.includes('excel')) {
    return FiFileText
  }

  if (mimeType.startsWith('image/')) {
    return FiImage
  }

  return FiFile
}

const getFileIconColor = (mimeType?: string) => {
  if (!mimeType) return 'gray.500'

  if (mimeType === 'application/pdf') {
    return 'red.500'
  }

  if (mimeType.includes('sheet') || mimeType.includes('excel')) {
    return 'green.500'
  }

  if (mimeType.startsWith('image/')) {
    return 'purple.500'
  }

  return 'gray.500'
}

function TreeFileItem({ file, level, onFileSelect, pickerMode = false }: TreeFileItemProps) {
  const dispatch = useAppDispatch()
  const toast = useToast()
  const { t } = useAppTranslation(['files', 'common'])
  const [isBeingDragged, setIsBeingDragged] = useState(false)

  // Rename state
  const [isRenaming, setIsRenaming] = useState(false)
  const [newName, setNewName] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  // Context menu state
  const [showContextMenu, setShowContextMenu] = useState(false)
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 })

  // Properties modal state
  const [showPropertiesModal, setShowPropertiesModal] = useState(false)

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  // Permissions modal state
  const [showPermissionsModal, setShowPermissionsModal] = useState(false)

  const handleDownload = useCallback(async () => {
    try {
      const downloadUrl = await apiService.generateSecureFileUrl(file.id, 'download')
      window.open(downloadUrl, '_blank')
    } catch (error) {
      console.error('Failed to generate download URL:', error)
      // Fallback to legacy method
      const fallbackUrl = apiService.getFileDownloadUrl(file.id)
      window.open(fallbackUrl, '_blank')
    }
  }, [file.id])

  // Rename functionality
  const handleRename = useCallback(() => {
    setNewName(file.display_name || file.original_name)
    setIsRenaming(true)
  }, [file.display_name, file.original_name])

  const handleRenameSubmit = useCallback(async () => {
    if (newName.trim() && newName.trim() !== (file.display_name || file.original_name)) {
      try {
        await dispatch(renameFile({ fileId: file.id, displayName: newName.trim() })).unwrap()
        toast({
          title: t('folderTree.file_renamed', { ns: 'files' }),
          description: t('folderTree.file_renamed_description', {
            ns: 'files',
            name: newName.trim(),
          }),
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
      } catch (error) {
        console.error('Failed to rename file:', error)
        toast({
          title: t('folderTree.rename_failed', { ns: 'files' }),
          description: t('folderTree.rename_failed_description', { ns: 'files' }),
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
      }
    }
    setIsRenaming(false)
    setNewName('')
  }, [dispatch, file.id, file.display_name, file.original_name, newName, toast, t])

  const handleRenameCancel = useCallback(() => {
    setIsRenaming(false)
    setNewName('')
  }, [])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleRenameSubmit()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        handleRenameCancel()
      }
    },
    [handleRenameSubmit, handleRenameCancel]
  )

  // Auto-focus input when renaming starts
  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isRenaming])

  // Context menu handlers
  const handleContextMenu = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault()
      event.stopPropagation()

      if (isRenaming) {
        return
      }

      setContextMenuPosition({
        x: event.clientX,
        y: event.clientY,
      })
      setShowContextMenu(true)
    },
    [isRenaming]
  )

  const handleCloseContextMenu = useCallback(() => {
    setShowContextMenu(false)
  }, [])

  const handleContextMenuRename = useCallback(() => {
    handleRename()
    handleCloseContextMenu()
  }, [handleRename, handleCloseContextMenu])

  const handleDeleteConfirm = useCallback(async () => {
    if (!file) return

    setIsDeleting(true)

    try {
      await dispatch(
        deleteFile({
          fileId: file.id,
          folderId: file.folder_id || null,
        })
      ).unwrap()

      toast({
        title: t('fileActions.deleteSuccess', { ns: 'files' }),
        description: t('fileActions.deleteSuccessDescription', {
          ns: 'files',
          fileName: file.display_name || file.original_name,
        }),
        status: 'success',
        duration: 3000,
        isClosable: true,
      })

      setShowDeleteModal(false)
    } catch (error) {
      console.error('Failed to delete file:', error)
      toast({
        title: t('fileActions.deleteError', { ns: 'files' }),
        description: t('fileActions.deleteErrorDescription', { ns: 'files' }),
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsDeleting(false)
    }
  }, [dispatch, file, toast, t])

  const handleDragStart = (e: React.DragEvent) => {
    if (isRenaming) {
      e.preventDefault()
      return
    }

    // Create custom drag image
    const dragImage = document.createElement('div')
    dragImage.style.position = 'absolute'
    dragImage.style.top = '-1000px'
    dragImage.style.left = '-1000px'
    dragImage.style.padding = '8px 12px'
    dragImage.style.backgroundColor = '#2b6cb0'
    dragImage.style.color = 'white'
    dragImage.style.borderRadius = '8px'
    dragImage.style.fontSize = '14px'
    dragImage.style.fontWeight = '600'
    dragImage.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)'
    dragImage.textContent = `📄 ${t('folderTree.moving_file', { ns: 'files' })}`

    document.body.appendChild(dragImage)
    e.dataTransfer.setDragImage(dragImage, 60, 20)

    setTimeout(() => {
      if (document.body.contains(dragImage)) {
        document.body.removeChild(dragImage)
      }
    }, 0)

    e.dataTransfer.setData(
      'application/json',
      JSON.stringify({
        type: 'file',
        id: file.id,
        name: file.original_name,
      })
    )
    e.dataTransfer.effectAllowed = 'move'
    setIsBeingDragged(true)
    e.stopPropagation()
  }

  const handleDragEnd = () => {
    setIsBeingDragged(false)
  }

  const handleClick = (e: React.MouseEvent) => {
    // Allow Cmd+Click and Ctrl+Click to open in new tab (handled by Link)
    if (e.metaKey || e.ctrlKey) {
      return
    }

    // Prevent navigation if dragging or renaming
    if (isBeingDragged || isRenaming) {
      e.preventDefault()
      return
    }

    // Call existing file select handler for regular clicks
    if (onFileSelect) {
      e.preventDefault()
      onFileSelect(file)
    }
  }

  return (
    <>
      <Link
        as={RouterLink}
        to={`/preview/${file.id}`}
        w="full"
        display="block"
        textDecoration="none"
        _hover={{ textDecoration: 'none' }}
        onClick={handleClick}
      >
        <Button
          as="div"
          variant="ghost"
          w="full"
          h="auto"
          py={1}
          px={2}
          justifyContent="flex-start"
          borderRadius="lg"
          opacity={isBeingDragged ? 0.5 : 1}
          _hover={{
            bg: isRenaming ? 'transparent' : 'gray.200',
            color: 'gray.900',
          }}
          transition="all 0.15s"
          draggable={pickerMode ? false : !isRenaming}
          onDragStart={pickerMode ? undefined : handleDragStart}
          onDragEnd={pickerMode ? undefined : handleDragEnd}
          onContextMenu={handleContextMenu}
          style={{ paddingLeft: `${(level + 1) * 16 + 8}px` }}
          cursor={isRenaming ? 'default' : 'pointer'}
        >
          <HStack spacing={2} w="full" borderRadius="lg" py={1} px={2}>
            <Icon
              as={getFileIcon(file.mime_type)}
              color={getFileIconColor(file.mime_type)}
              fontSize="sm"
              flexShrink={0}
            />
            <Box flex="1" overflow="hidden">
              {isRenaming ? (
                <Input
                  ref={inputRef}
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={handleRenameCancel}
                  size="sm"
                  fontSize="sm"
                  fontWeight="400"
                  variant="outline"
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <Text
                  fontSize="sm"
                  fontWeight="400"
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  textAlign="left"
                  color="gray.600"
                >
                  {file.display_name || file.original_name}
                </Text>
              )}
            </Box>
            {/* Tags Icon - Show if file has tags */}
            {file.tags && file.tags.length > 0 && (
              <Icon as={FiTag} color="blue.500" fontSize="xs" flexShrink={0} ml="auto" />
            )}
          </HStack>
        </Button>
      </Link>

      {/* Context Menu */}
      <ContextMenu
        isOpen={showContextMenu}
        position={contextMenuPosition}
        onClose={handleCloseContextMenu}
        items={[
          {
            label: t('buttons.rename', { ns: 'common' }),
            onClick: handleContextMenuRename,
          },
          {
            label: t('buttons.download', { ns: 'common' }),
            onClick: handleDownload,
          },
          {
            label: t('buttons.properties', { ns: 'common' }),
            onClick: () => setShowPropertiesModal(true),
          },
          {
            label: t('fileActions.share', { ns: 'files' }),
            onClick: () => setShowPermissionsModal(true),
          },
          {
            label: t('buttons.delete', { ns: 'common' }),
            onClick: () => setShowDeleteModal(true),
            isDestructive: true,
          },
        ]}
      />

      {/* File Properties Modal */}
      <FilePropertiesModal
        isOpen={showPropertiesModal}
        onClose={() => setShowPropertiesModal(false)}
        file={file}
      />

      {/* Delete Confirmation Modal */}
      <DeleteFileConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        file={file}
        isLoading={isDeleting}
      />

      {/* File Permissions Modal */}
      <FilePermissionsModal
        isOpen={showPermissionsModal}
        onClose={() => setShowPermissionsModal(false)}
        fileId={file.id}
        fileName={file.display_name || file.original_name}
      />
    </>
  )
}

function TreeFolderItem({
  folder,
  selectedFolder,
  onFolderSelect,
  onFileSelect,
  level,
  expandedFolders,
  toggleFolderExpansion,
  moveFolder,
  moveFile,
  moveSelectedFiles,
  pickerMode = false,
}: TreeFolderItemProps) {
  const dispatch = useAppDispatch()
  const toast = useToast()
  const { t } = useAppTranslation(['files', 'common'])
  const [isDragOver, setIsDragOver] = useState(false)
  const [isBeingDragged, setIsBeingDragged] = useState(false)

  // Rename state
  const [isRenaming, setIsRenaming] = useState(false)
  const [newName, setNewName] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  // Context menu state
  const [showContextMenu, setShowContextMenu] = useState(false)
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 })

  // Properties modal state
  const [showPropertiesModal, setShowPropertiesModal] = useState(false)

  // Permissions modal state
  const [showPermissionsModal, setShowPermissionsModal] = useState(false)

  // Get files from Redux state instead of static folder.files
  const filesFromRedux = useAppSelector((state) => selectFilesByFolder(state, folder.id))
  const filesLoading = useAppSelector((state) => selectFilesLoading(state, folder.id))

  // Check if files have been loaded for this folder (exists in Redux state)
  const filesHaveBeenLoaded = useAppSelector((state) =>
    state.files.byFolderId.hasOwnProperty(folder.id)
  )

  const isExpanded = expandedFolders.has(folder.id)
  const isSelected = selectedFolder?.id === folder.id

  const subfolders = folder.subfolders || folder.children || []
  const files = filesFromRedux // Use Redux state files instead of folder.files

  // Sort subfolders alphabetically for better UX, memoized for performance
  const sortedSubfolders = useMemo(() => {
    return [...subfolders].sort((a, b) => {
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    })
  }, [subfolders])

  // Sort files alphabetically for better UX, memoized for performance
  const sortedFiles = useMemo(() => {
    return [...files].sort((a, b) => {
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    })
  }, [files])

  const hasChildren =
    (folder.subfolders && folder.subfolders.length > 0) ||
    (folder.children && folder.children.length > 0) ||
    (files && files.length > 0)

  // Rename functionality
  const handleRename = useCallback(() => {
    setNewName(folder.name)
    setIsRenaming(true)
  }, [folder.name])

  const handleRenameSubmit = useCallback(async () => {
    if (newName.trim() && newName.trim() !== folder.name) {
      try {
        await dispatch(
          updateFolder({
            folderId: folder.id,
            data: { name: newName.trim() },
          })
        ).unwrap()
        toast({
          title: t('folderTree.folder_renamed', { ns: 'files' }),
          description: t('folderTree.folder_renamed_description', {
            ns: 'files',
            name: newName.trim(),
          }),
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
      } catch (error) {
        console.error('Failed to rename folder:', error)
        toast({
          title: t('folderTree.rename_failed', { ns: 'files' }),
          description: t('folderTree.rename_failed_description', { ns: 'files' }),
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
      }
    }
    setIsRenaming(false)
    setNewName('')
  }, [dispatch, folder.id, folder.name, newName, toast, t])

  const handleRenameCancel = useCallback(() => {
    setIsRenaming(false)
    setNewName('')
  }, [])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleRenameSubmit()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        handleRenameCancel()
      }
    },
    [handleRenameSubmit, handleRenameCancel]
  )

  // Auto-focus input when renaming starts
  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isRenaming])

  // Context menu handlers
  const handleContextMenu = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault()
      event.stopPropagation()

      if (isRenaming) {
        return
      }

      setContextMenuPosition({
        x: event.clientX,
        y: event.clientY,
      })
      setShowContextMenu(true)
    },
    [isRenaming]
  )

  const handleCloseContextMenu = useCallback(() => {
    setShowContextMenu(false)
  }, [])

  const handleContextMenuRename = useCallback(() => {
    handleRename()
    handleCloseContextMenu()
  }, [handleRename, handleCloseContextMenu])

  const handleContextMenuProperties = useCallback(() => {
    setShowPropertiesModal(true)
    handleCloseContextMenu()
  }, [handleCloseContextMenu])

  const handleDownloadFolder = useCallback(() => {
    const downloadUrl = apiService.getFolderDownloadZipUrl(folder.id)
    window.open(downloadUrl, '_blank')
    toast({
      title: t('folders.downloadStarted', { ns: 'files' }),
      description: t('folders.downloadStartedDescription', { ns: 'files', folderName: folder.name }),
      status: 'info',
      duration: 5000,
      isClosable: true,
    })
  }, [folder.id, folder.name, toast, t])

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false)
    }
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)

    const jsonData = e.dataTransfer.getData('application/json')

    if (!jsonData) {
      const nativeFiles = e.dataTransfer.files
      if (nativeFiles?.length > 0) {
        let uploaded = 0
        let failed = 0
        for (const file of Array.from(nativeFiles)) {
          try {
            const result = await apiService.uploadFile(file, folder.id)
            dispatch(addFile({ file: result, folderId: folder.id }))
            uploaded++
          } catch {
            failed++
          }
        }
        if (uploaded > 0) {
          toast({
            title: t('folderTree.files_uploaded', { ns: 'files', defaultValue: 'Files uploaded' }),
            description: t('folderTree.files_uploaded_description', {
              ns: 'files',
              count: uploaded,
              name: folder.name,
              defaultValue: `${uploaded} file(s) uploaded to ${folder.name}`,
            }),
            status: 'success',
            duration: 3000,
            isClosable: true,
          })
        }
        if (failed > 0) {
          toast({
            title: t('folderTree.upload_failed', { ns: 'files', defaultValue: 'Upload failed' }),
            description: t('folderTree.upload_failed_description', {
              ns: 'files',
              count: failed,
              defaultValue: `${failed} file(s) failed to upload`,
            }),
            status: 'error',
            duration: 3000,
            isClosable: true,
          })
        }
      }
      return
    }

    try {
      const dragData = JSON.parse(jsonData)
      const { type, id, name } = dragData

      // Prevent dropping on itself
      if (type === 'folder' && id === folder.id) {
        return
      }

      let success = false
      if (type === 'files') {
        success = await moveSelectedFiles(folder.id)

        if (success) {
          const fileCount = dragData.fileIds?.length || 1
          toast({
            title: t('folderTree.files_moved'),
            description: t('folderTree.files_moved_description', {
              count: fileCount,
              name: folder.name,
            }),
            status: 'success',
            duration: 3000,
            isClosable: true,
          })
        }
      } else if (type === 'file') {
        success = await moveFile(id, folder.id)

        if (success) {
          toast({
            title: t('folderTree.file_moved'),
            description: t('folderTree.file_moved_description', {
              name: name,
              folderName: folder.name,
            }),
            status: 'success',
            duration: 3000,
            isClosable: true,
          })
        }
      } else if (type === 'folder') {
        success = await moveFolder(id, folder.id)

        if (success) {
          toast({
            title: t('folderTree.folder_moved', { ns: 'files' }),
            description: t('folderTree.folder_moved_description', { ns: 'files', name: name, folderName: folder.name }),
            status: 'success',
            duration: 3000,
            isClosable: true,
          })
        }
      }

      if (!success) {
        toast({
          title: t('folderTree.move_failed', { ns: 'files' }),
          description: t('folderTree.move_failed_description', {
            ns: 'files',
            type: type === 'files' ? t('folderTree.files', { ns: 'files' }) : name,
          }),
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
      }
    } catch (error) {
      console.error('Error parsing drag data:', error)
      toast({
        title: t('folderTree.move_failed', { ns: 'files' }),
        description: t('folderTree.invalid_drag_data', { ns: 'files' }),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const handleDragStart = (e: React.DragEvent) => {
    if (isRenaming) {
      e.preventDefault()
      return
    }

    const dragImage = document.createElement('div')
    dragImage.style.position = 'absolute'
    dragImage.style.top = '-1000px'
    dragImage.style.left = '-1000px'
    dragImage.style.padding = '8px 12px'
    dragImage.style.backgroundColor = '#2b6cb0'
    dragImage.style.color = 'white'
    dragImage.style.borderRadius = '8px'
    dragImage.style.fontSize = '14px'
    dragImage.style.fontWeight = '600'
    dragImage.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)'
    dragImage.textContent = `📁 ${t('folderTree.moving_folder', { ns: 'files' })}`

    document.body.appendChild(dragImage)
    e.dataTransfer.setDragImage(dragImage, 60, 20)

    setTimeout(() => {
      if (document.body.contains(dragImage)) {
        document.body.removeChild(dragImage)
      }
    }, 0)

    e.dataTransfer.setData(
      'application/json',
      JSON.stringify({
        type: 'folder',
        id: folder.id,
        name: folder.name,
      })
    )
    e.dataTransfer.effectAllowed = 'move'
    setIsBeingDragged(true)
    e.stopPropagation()
  }

  const handleDragEnd = () => {
    setIsBeingDragged(false)
  }

  const handleFolderClick = (e: React.MouseEvent) => {
    if (!isBeingDragged && !isRenaming) {
      // In picker mode, selecting a folder should expand it so loaded files/subfolders are visible
      // without requiring a separate chevron click (row click alone used to fetch but leave collapsed).
      if (pickerMode && !isExpanded) {
        toggleFolderExpansion(folder.id)
      }
      onFolderSelect(folder)
    }
    e.stopPropagation()
  }

  const handleToggleExpansion = (e: React.MouseEvent) => {
    e.stopPropagation()
    toggleFolderExpansion(folder.id)

    // If expanding and files haven't been loaded yet, trigger folder selection to fetch files
    const willBeExpanded = !isExpanded
    if (willBeExpanded && !filesHaveBeenLoaded && !filesLoading) {
      onFolderSelect(folder)
    }
  }

  return (
    <Box>
      <Button
        variant="ghost"
        w="full"
        h="auto"
        py={2}
        px={2}
        justifyContent="flex-start"
        borderRadius="lg"
        bg={isSelected ? 'gray.200' : isDragOver ? 'blue.100' : 'transparent'}
        color={isSelected ? 'gray.900' : 'gray.700'}
        borderColor={isDragOver ? 'blue.400' : 'transparent'}
        borderWidth={isDragOver ? '2px' : '0px'}
        borderStyle={isDragOver ? 'dashed' : 'solid'}
        opacity={isBeingDragged ? 0.5 : 1}
        _hover={{
          bg: isSelected
            ? 'gray.300'
            : isDragOver
              ? 'blue.200'
              : isRenaming
                ? 'transparent'
                : 'gray.200',
          color: 'gray.900',
        }}
        transition="all 0.15s"
        onClick={handleFolderClick}
        draggable={pickerMode ? false : !isRenaming}
        onDragStart={pickerMode ? undefined : handleDragStart}
        onDragEnd={pickerMode ? undefined : handleDragEnd}
        onDragOver={pickerMode ? undefined : handleDragOver}
        onDragLeave={pickerMode ? undefined : handleDragLeave}
        onDrop={pickerMode ? undefined : handleDrop}
        onContextMenu={handleContextMenu}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        cursor={isRenaming ? 'default' : 'pointer'}
      >
        <HStack spacing={2} w="full">
          {hasChildren && (
            <Icon
              as={isExpanded ? FiChevronDown : FiChevronRight}
              fontSize="xs"
              color="gray.500"
              cursor="pointer"
              onClick={handleToggleExpansion}
              flexShrink={0}
            />
          )}
          {!hasChildren && <Box w="12px" flexShrink={0} />}
          <Icon
            as={FiFolder}
            color={isSelected ? 'brand.600' : 'brand.500'}
            fontSize="sm"
            flexShrink={0}
          />
          <Box flex="1" overflow="hidden">
            {isRenaming ? (
              <Input
                ref={inputRef}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleRenameCancel}
                size="sm"
                fontSize="sm"
                fontWeight="500"
                variant="outline"
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <Text
                fontSize="sm"
                fontWeight="500"
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
                textAlign="left"
              >
                {folder.name}
              </Text>
            )}
          </Box>
          <Tooltip
            label={
              typeof folder.total_file_count === 'number'
                ? t('folderTree.folder_tooltip_with_total', {
                    ns: 'files',
                    fileCount: folder.total_file_count,
                    folderCount: subfolders.length,
                  })
                : t('folderTree.folder_tooltip', {
                    ns: 'files',
                    fileCount: files ? files.length : 0,
                    folderCount: subfolders.length,
                  })
            }
            hasArrow
            placement="top"
          >
            <Text fontSize="xs" color="gray.500" flexShrink={0}>
              {(() => {
                // Use API-provided total_file_count if available (includes recursive count)
                if (typeof folder.total_file_count === 'number') {
                  // Show total file count + subfolder count for comprehensive overview
                  return folder.total_file_count + subfolders.length
                }

                // Fallback to current logic for backwards compatibility
                return subfolders.length + (files ? files.length : 0)
              })()}
            </Text>
          </Tooltip>
        </HStack>
      </Button>

      {/* Context Menu */}
      <ContextMenu
        isOpen={showContextMenu}
        position={contextMenuPosition}
        onClose={handleCloseContextMenu}
        items={[
          {
            label: t('folderTree.rename', { ns: 'files' }),
            onClick: handleContextMenuRename,
          },
          {
            label: t('folderTree.properties', { ns: 'files' }),
            onClick: handleContextMenuProperties,
          },
          {
            label: t('folders.downloadAsZip', { ns: 'files' }),
            onClick: handleDownloadFolder,
          },
          {
            label: t('buttons.share', { ns: 'common' }),
            onClick: () => setShowPermissionsModal(true),
          },
        ]}
      />

      {/* Folder Properties Modal */}
      <FolderPropertiesModal
        isOpen={showPropertiesModal}
        onClose={() => setShowPropertiesModal(false)}
        folderId={folder.id}
      />

      {/* Folder Permissions Modal */}
      <FolderPermissionsModal
        isOpen={showPermissionsModal}
        onClose={() => setShowPermissionsModal(false)}
        folderId={folder.id}
        folderName={folder.name}
      />

      {hasChildren && (
        <Collapse in={isExpanded} animateOpacity unmountOnExit={pickerMode}>
          <VStack spacing={0} align="stretch">
            {/* Render subfolders */}
            {sortedSubfolders.map((subfolder) => (
              <TreeFolderItem
                key={subfolder.id}
                folder={subfolder}
                selectedFolder={selectedFolder}
                onFolderSelect={onFolderSelect}
                onFileSelect={onFileSelect}
                level={level + 1}
                expandedFolders={expandedFolders}
                toggleFolderExpansion={toggleFolderExpansion}
                moveFolder={moveFolder}
                moveFile={moveFile}
                moveSelectedFiles={moveSelectedFiles}
                pickerMode={pickerMode}
              />
            ))}

            {/* Render files */}
            {sortedFiles.map((file) => (
              <TreeFileItem
                key={file.id}
                file={file}
                level={level + 1}
                onFileSelect={onFileSelect}
                pickerMode={pickerMode}
              />
            ))}
          </VStack>
        </Collapse>
      )}
    </Box>
  )
}

const FolderTree = memo(function FolderTree({
  folders,
  selectedFolder,
  onFolderSelect,
  onFileSelect,
  pickerMode = false,
}: FolderTreeProps) {
  const dispatch = useAppDispatch()
  const toast = useToast()
  const { t } = useAppTranslation(['files', 'common'])
  const [isRootDragOver, setIsRootDragOver] = useState(false)

  const { expandedFolders, toggleFolderExpansion, moveFolder, moveFile, moveSelectedFiles } =
    useNavigation()

  // Sort root folders alphabetically for better UX, memoized for performance
  const sortedFolders = useMemo(() => {
    return [...folders].sort((a, b) => {
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    })
  }, [folders])

  // Root folder drag and drop handlers
  const handleRootDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setIsRootDragOver(true)
  }

  const handleRootDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsRootDragOver(false)
    }
  }

  const handleRootDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    setIsRootDragOver(false)

    const jsonData = e.dataTransfer.getData('application/json')

    if (!jsonData) {
      const nativeFiles = e.dataTransfer.files
      if (nativeFiles?.length > 0) {
        let uploaded = 0
        let failed = 0
        for (const file of Array.from(nativeFiles)) {
          try {
            const result = await apiService.uploadFile(file)
            dispatch(addFile({ file: result, folderId: null }))
            uploaded++
          } catch {
            failed++
          }
        }
        if (uploaded > 0) {
          toast({
            title: t('folderTree.files_uploaded', { ns: 'files', defaultValue: 'Files uploaded' }),
            description: t('folderTree.files_uploaded_to_root_description', {
              ns: 'files',
              count: uploaded,
              defaultValue: `${uploaded} file(s) uploaded to root`,
            }),
            status: 'success',
            duration: 3000,
            isClosable: true,
          })
        }
        if (failed > 0) {
          toast({
            title: t('folderTree.upload_failed', { ns: 'files', defaultValue: 'Upload failed' }),
            description: t('folderTree.upload_failed_description', {
              ns: 'files',
              count: failed,
              defaultValue: `${failed} file(s) failed to upload`,
            }),
            status: 'error',
            duration: 3000,
            isClosable: true,
          })
        }
      }
      return
    }

    try {
      const dragData = JSON.parse(jsonData)
      const { type, id, name } = dragData

      let success = false
      if (type === 'files') {
        success = await moveSelectedFiles() // Move to root (no folderId)

        if (success) {
          const fileCount = dragData.fileIds?.length || 1
          toast({
            title: t('folderTree.files_moved', { ns: 'files' }),
            description: t('folderTree.files_moved_to_root_description', {
              ns: 'files',
              count: fileCount,
            }),
            status: 'success',
            duration: 3000,
            isClosable: true,
          })
        }
      } else if (type === 'file') {
        success = await moveFile(id) // Move to root (no targetFolderId)

        if (success) {
          toast({
            title: t('folderTree.file_moved', { ns: 'files' }),
            description: t('folderTree.file_moved_to_root_description', {
              ns: 'files',
              name: name,
            }),
            status: 'success',
            duration: 3000,
            isClosable: true,
          })
        }
      } else if (type === 'folder') {
        success = await moveFolder(id) // Move to root (no targetParentId)

        if (success) {
          toast({
            title: t('folderTree.folder_moved', { ns: 'files' }),
            description: t('folderTree.folder_moved_to_root_description', {
              ns: 'files',
              name: name,
            }),
            status: 'success',
            duration: 3000,
            isClosable: true,
          })
        }
      }

      if (!success) {
        toast({
          title: t('folderTree.move_failed', { ns: 'files' }),
          description: t('folderTree.move_failed_description', {
            ns: 'files',
            type: type === 'files' ? t('folderTree.files', { ns: 'files' }) : name,
          }),
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
      }
    } catch (error) {
      console.error('Error parsing drag data:', error)
      toast({
        title: t('folderTree.move_failed', { ns: 'files' }),
        description: t('folderTree.invalid_drag_data', { ns: 'files' }),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  if (folders.length === 0) {
    return (
      <MotionBox initial={{ opacity: 0 }} animate={{ opacity: 1 }} p={4} textAlign="center">
        <Text fontSize="sm" color="gray.600" fontStyle="italic">
          {t('folderTree.no_folders_available', { ns: 'files' })}
        </Text>
      </MotionBox>
    )
  }

  return (
    <MotionBox
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <VStack spacing={1} align="stretch">
        {/* Root folder option */}
        <Button
          variant="ghost"
          w="full"
          h="auto"
          py={2}
          px={2}
          justifyContent="flex-start"
          borderRadius="lg"
          bg={selectedFolder === null ? 'gray.200' : isRootDragOver ? 'blue.100' : 'transparent'}
          color={selectedFolder === null ? 'gray.900' : 'gray.700'}
          borderColor={isRootDragOver ? 'blue.400' : 'transparent'}
          borderWidth={isRootDragOver ? '2px' : '0px'}
          borderStyle={isRootDragOver ? 'dashed' : 'solid'}
          _hover={{
            bg: selectedFolder === null ? 'gray.300' : isRootDragOver ? 'blue.200' : 'gray.200',
            color: 'gray.900',
          }}
          transition="all 0.15s"
          onClick={() => onFolderSelect(null)}
          onDragOver={pickerMode ? undefined : handleRootDragOver}
          onDragLeave={pickerMode ? undefined : handleRootDragLeave}
          onDrop={pickerMode ? undefined : handleRootDrop}
        >
          <HStack spacing={2}>
            <Icon as={FiFolder} color="brand.500" fontSize="sm" />
            <Text fontSize="sm" fontWeight="500">
              {t('folderTree.all_files', { ns: 'files' })}
            </Text>
          </HStack>
        </Button>

        {/* Folder tree */}
        {sortedFolders.map((folder) => (
          <TreeFolderItem
            key={folder.id}
            folder={folder}
            selectedFolder={selectedFolder}
            onFolderSelect={onFolderSelect}
            onFileSelect={onFileSelect}
            level={0}
            expandedFolders={expandedFolders}
            toggleFolderExpansion={toggleFolderExpansion}
            moveFolder={moveFolder}
            moveFile={moveFile}
            moveSelectedFiles={moveSelectedFiles}
            pickerMode={pickerMode}
          />
        ))}
      </VStack>
    </MotionBox>
  )
})

export default FolderTree
